import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTools } from '../../ToolProvider.jsx'
import { useEditorTypeRightContext } from '../../SelectedTypeProvider.jsx'
import ColorCarousel from '../ColorCarousel/ColorCarousel.jsx'
import TextEditButton from '../TextEditButton/TextEditButton.jsx'
import CotasDock from '../CotasDock/CotasDock.jsx'
import FlipFaceButton from './FlipFaceButton.jsx'
import ZoomControl from './ZoomControl.jsx'

/**
 * Dock de abajo a la derecha del editor 2D (C3D-5): una sola píldora con tres
 * grupos, de izquierda a derecha — herramientas de la selección (color, texto;
 * solo existen con algo seleccionado), vista (dar la vuelta, cotas) y zoom.
 *
 * Va a la derecha porque es donde cae el pulgar en el móvil apaisado y deja la
 * izquierda para la barra de herramientas. El panel derecho se dibuja encima
 * del lienzo, así que cuando se abre el dock se desplaza para quedar a su
 * izquierda en vez de quedar tapado.
 */
const ViewportDock = ({ leftDockRef, rightPanelRef }) => {
    const { onResetViewport } = useTools()
    const editorTypeRight = useEditorTypeRightContext()
    const [panelWidth, setPanelWidth] = useState(0)

    // El panel derecho solo existe con un editor abierto; se mide su ancho real
    // (cambia según el panel y el tamaño de la pantalla).
    useEffect(() => {
        const panel = rightPanelRef?.current
        if (!panel) {
            setPanelWidth(0)
            return undefined
        }
        const measure = () => setPanelWidth(panel.getBoundingClientRect().width)
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(panel)
        return () => observer.disconnect()
    }, [rightPanelRef, editorTypeRight?.type])

    // La barra izquierda y el panel derecho se dibujan ENCIMA del canvas
    // (que ocupa toda la pantalla), así que "centrar" sin más deja el
    // dibujo detrás del panel derecho cuando está abierto. Se miden los
    // anchos reales y se pasan como insets a resetViewport.
    const handleFit = () => {
        const leftInset = leftDockRef?.current?.getBoundingClientRect().width ?? 0
        const rightPanelRect = rightPanelRef?.current?.getBoundingClientRect()
        const rightInset = rightPanelRect ? window.innerWidth - rightPanelRect.left : 0
        onResetViewport({ leftInset, rightInset })
    }

    return (
        <div
            className="customiz3r-viewport-dock"
            style={panelWidth ? { '--dock-right': `${panelWidth + 8}px` } : undefined}
        >
            <div className="customiz3r-viewport-dock__pill" role="toolbar" aria-label="Herramientas rápidas y vista 2D">
                {/* Sin hijos mientras no haya selección: el grupo se oculta solo (:empty). */}
                <div className="customiz3r-viewport-dock__group customiz3r-viewport-dock__group--selection">
                    <ColorCarousel />
                    <TextEditButton />
                </div>
                <div className="customiz3r-viewport-dock__group">
                    <FlipFaceButton />
                    <CotasDock />
                </div>
                <ZoomControl onFit={handleFit} />
            </div>
        </div>
    )
}

ViewportDock.propTypes = {
    leftDockRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    rightPanelRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
}

export default ViewportDock
